import React from 'react';
import { motion } from 'framer-motion';
import { IMAGES } from '@/assets/images';
import { useLanguage } from '@/hooks/useLanguage';
import { COMPANY_PHILOSOPHY } from '@/data/index';
import { Heart, Globe, ShieldCheck } from 'lucide-react';

const Philosophy = () => {
  const { language } = useLanguage();
  const philosophy = COMPANY_PHILOSOPHY;

  // Icons mapping for philosophy points
  const getIcon = (index: number) => {
    switch (index) {
      case 0: return <Heart className="w-8 h-8 text-primary" />;
      case 1: return <Globe className="w-8 h-8 text-primary" />;
      case 2: return <ShieldCheck className="w-8 h-8 text-primary" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.OFFICE_BUILDING_3}
            alt="Corporate Background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-bold tracking-tight text-foreground"
          >
            {language === 'en' ? 'Philosophy' : '企業理念'}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mt-4 h-1 w-12 bg-primary mx-auto"
          />
        </div>
      </section>

      {/* Main Vision Statement */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-4xl font-bold leading-tight text-foreground mb-8">
              {language === 'en' ? philosophy.mainTitle.en : philosophy.mainTitle.ja}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              {language === 'en' ? philosophy.description.en : philosophy.description.ja}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Points Grid */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {philosophy.points.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                className="flex flex-col items-center text-center space-y-6"
              >
                <div className="p-4 bg-background rounded-full border border-border shadow-sm">
                  {getIcon(index)}
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-foreground">
                    {language === 'en' ? point.title.en : point.title.ja}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {language === 'en' ? point.detail.en : point.detail.ja}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability & Long-term commitment */}
      <section className="py-24 border-t border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h3 className="text-2xl font-bold">
                {language === 'en' ? 'Long-term Management' : '持続可能な経営'}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {language === 'en' 
                  ? "We believe that true business success is measured over decades, not quarters. By focusing on animal welfare and cultural harmony, we build foundations that last."
                  : "私たちは、真の事業の成功は四半期単位ではなく、数十年という長いスパンで測られるべきだと考えています。動物福祉と文化の調和に焦点を当てることで、永続的な基盤を築きます。"}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {language === 'en'
                  ? "Integrity and transparency are the cornerstones of our relationship with the community and our partners."
                  : "誠実さと透明性は、地域社会やパートナーとの関係を築く上での礎です。"}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-video bg-muted rounded-xl overflow-hidden border border-border"
            >
              <img
                src={IMAGES.OFFICE_BUILDING_3}
                alt="Architecture"
                className="w-full h-full object-cover grayscale opacity-80"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Philosophy;